import type { PendingAttachment } from '@Ai/components/chatTypes';
import type { PageSelectionContext } from '@Ai/stores/pageAssistantHandoffStore';
import type { ResolvedTheme } from '@Core/hooks/useTheme';

/**
 * Same-origin postMessage protocol between the page editor (parent window) and
 * the draft preview it embeds in an iframe (`/preview/pages/{page}` rendering
 * `ShowPage`). The parent pushes the working draft code, the design-mode
 * toggle and the preview-only theme down; the child sends design-mode edits and AI-assistant handoffs
 * back up. Edits are not saved over the bridge: they land in the editor's
 * draft state and the editor's autosave persists them.
 *
 * Every message carries the page id: the child re-announces itself with a
 * `ready` whenever a page mounts in the iframe, so when the author
 * navigates the preview to a different page the editor follows it (and
 * any other stale-page traffic is dropped). A `ready` also reports the concrete
 * location the document reached, so the editor adopts the route param values
 * the author navigated to instead of only the page. Both sides post with an
 * explicit same-origin target so external embeds of a page never see editor
 * traffic.
 */
export const EDITOR_PREVIEW_MESSAGE_TYPE = 'diagonal-page-editor-preview';

type EditorPreviewMessageBase = {
  type: typeof EDITOR_PREVIEW_MESSAGE_TYPE;
  pageId: string;
};

export type EditorPreviewViewer = {
  id: string;
  resourceId: string;
  displayName: string;
};

export type EditorPreviewParentMessage = EditorPreviewMessageBase &
  (
    | {
        event: 'init';
        code: string;
        isDesignModeEnabled: boolean;
        // Optional on the wire: an editor tab still running a bundle from
        // before the preview theme override omits it entirely.
        theme?: ResolvedTheme | null;
        // Whether the Preview view is showing the frame. Optional on the wire
        // for the same reason as `theme`; absent reads as visible.
        isVisible?: boolean;
        // Whether the author can open the component and layout editors.
        // Optional on the wire for the same reason as `theme`; absent reads
        // as allowed.
        canOpenImportedSources?: boolean;
      }
    | { event: 'set-code'; code: string }
    | { event: 'set-design-mode'; isEnabled: boolean }
    // The editor keeps the frame mounted while the author works in Code or
    // Settings. A hidden document holds its own navigations, so a redirect the
    // page scheduled cannot move the preview while nobody is looking at it.
    | { event: 'set-visible'; isVisible: boolean }
    // A null theme releases the preview back to the editor's own theme.
    | { event: 'set-theme'; theme: ResolvedTheme | null }
    | { event: 'print' }
    // Settles a design-mode `pick-image` request; a null fileId means the
    // author dismissed the media library without choosing a file.
    | { event: 'image-picked'; requestId: string; fileId: string | null }
  );

export type EditorPreviewChildMessage = EditorPreviewMessageBase &
  // `routeParams` is null when the document's URL is not the page's authored
  // route — the draft preview route it is first mounted with, or a redirect —
  // so a location the author never navigated to cannot overwrite their inputs.
  (
    | {
        event: 'ready';
        viewer: EditorPreviewViewer | null;
        routeParams: Record<string, string> | null;
      }
    | { event: 'code-change'; code: string }
    | { event: 'design-mode-change'; isEnabled: boolean }
    | {
        event: 'selection-change';
        selectionContext: PageSelectionContext | null;
      }
    // The composer uploads inside the same-origin preview iframe, so only the
    // finished upload references travel — never the files themselves.
    | { event: 'ask-ai'; prompt: string; attachments: PendingAttachment[] }
    // Design mode needs a workspace file: the media library modal lives in the
    // editor document, so the child asks and awaits the `image-picked` reply.
    | { event: 'pick-image'; requestId: string }
  );

// Omit that distributes over each union member instead of collapsing the
// union to its common keys.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export type EditorPreviewParentMessagePayload = DistributiveOmit<
  EditorPreviewParentMessage,
  'type' | 'pageId'
>;

export type EditorPreviewChildMessagePayload = DistributiveOmit<
  EditorPreviewChildMessage,
  'type' | 'pageId'
>;

export function isEditorPreviewMessage(
  data: unknown
): data is EditorPreviewMessageBase & { event: string } {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const message = data as {
    type?: unknown;
    pageId?: unknown;
    event?: unknown;
  };

  return (
    message.type === EDITOR_PREVIEW_MESSAGE_TYPE &&
    typeof message.pageId === 'string' &&
    typeof message.event === 'string'
  );
}
